import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Target } from "@phosphor-icons/react";
import type { Goal, TargetAction } from "./index";

export interface TargetActionCardProps {
  /** Target to resolve before disposing the goal. */
  target: { id: string; name: string };
  /** Current action selected for this target. */
  action: TargetAction;
  /** ID of the destination goal when action is "move". */
  destinationGoalId: string;
  /** Goals available as migration destinations. */
  availableGoals: Goal[];
  /**
   * Label for the primary radio option (e.g. "Archive with this goal" or
   * "Delete with this goal"). Driven by the parent variant.
   */
  primaryActionLabel: string;
  /** Called whenever the user changes the action or destination goal. */
  onChange: (update: { action?: TargetAction; destinationGoalId?: string }) => void;
}

/**
 * Card representing a single active target that must be resolved before the
 * parent goal is archived or deleted. The user chooses to either dispose of
 * this target alongside the goal or move it to another goal.
 *
 * When "Move to another goal" is selected a destination `Select` is revealed.
 */
const TargetActionCard: React.FC<TargetActionCardProps> = ({
  target,
  action,
  destinationGoalId,
  availableGoals,
  primaryActionLabel,
  onChange,
}) => {
  const handleActionChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onChange({ action: value as TargetAction, destinationGoalId: "" });
  };

  const handleGoalChange = (e: SelectChangeEvent) => {
    onChange({ destinationGoalId: e.target.value });
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 4, borderColor: "divider" }}>
      {/* Target name header */}
      <CardHeader
        avatar={
          <Avatar
            aria-hidden="true"
            sx={{
              bgcolor: "action.hover",
              color: "text.secondary",
              width: 40,
              height: 40,
            }}
          >
            <Target size={20} />
          </Avatar>
        }
        title={target.name}
        titleTypographyProps={{ variant: "body1", color: "text.primary" }}
        sx={{ pb: 0 }}
      />

      {/* Action choices */}
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Radio group */}
          <FormControl>
            <FormLabel
              id={`target-action-label-${target.id}`}
              sx={{
                typography: "caption",
                fontWeight: 500,
                color: "text.secondary",
                mb: 1,
              }}
            >
              Choose what to do with this target
            </FormLabel>
            <RadioGroup
              aria-labelledby={`target-action-label-${target.id}`}
              value={action}
              onChange={handleActionChange}
              sx={{ gap: 1 }}
            >
              <FormControlLabel
                value="withGoal"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2" color="text.primary">
                    {primaryActionLabel}
                  </Typography>
                }
              />
              <FormControlLabel
                value="move"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2" color="text.primary">
                    Move to another goal
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* Destination goal — only visible when "move" is selected */}
          {action === "move" && (
            <FormControl fullWidth>
              <FormLabel
                id={`destination-goal-label-${target.id}`}
                sx={{
                  typography: "caption",
                  fontWeight: 500,
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Destination Goal
              </FormLabel>
              <Select
                labelId={`destination-goal-label-${target.id}`}
                displayEmpty
                value={destinationGoalId}
                onChange={handleGoalChange}
                size="small"
                aria-label="Select destination goal"
                renderValue={(value) =>
                  value ? (
                    availableGoals.find((g) => g.id === value)?.name ?? value
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Select a goal...
                    </Typography>
                  )
                }
                sx={{ borderRadius: "10px" }}
              >
                {availableGoals.map((goal) => (
                  <MenuItem key={goal.id} value={goal.id}>
                    {goal.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TargetActionCard;
